import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../authContext';
import useSelectedRadio from '../hooks/useSelectedRadio';
import { authFetch } from '../authFetch';
import API_BASE_URL from '../api';
import { useTranslation } from 'react-i18next';
import ExportButton from '../components/ExportButton';

export default function LtrServicePage() {
  const { isAuth } = useAuth();
  const { radioId, shortcode, name: stationName } = useSelectedRadio();
  const { t, i18n } = useTranslation('common');
  const serviceName = t('ltr.title');
  const exportLocale = resolveLocaleForFormatter(i18n?.language);
  const stationDisplayName = stationName || shortcode || '';
  const headingTitle = stationDisplayName ? t('ltr.heading', { station: stationDisplayName }) : serviceName;

  const [date, setDate] = useState('');
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState('');

  const exportStrings = React.useMemo(() => ({
    serviceName,
    stationLabel: t('ltr.station_label'),
    dateLabel: t('ltr.date_label_plain'),
    emptyExport: t('ltr.export_empty'),
    table: {
      start: t('ltr.table.start'),
      end: t('ltr.table.end'),
      clips: t('ltr.table.clips'),
      impressions: t('ltr.table.impressions'),
      completions: t('ltr.table.completions'),
      ltr: t('ltr.table.ltr'),
    },
  }), [serviceName, t]);

  // дефолтная дата — сегодня
  useEffect(() => {
    if (!date) {
      const today = new Date();
      setDate(today.toISOString().slice(0, 10));
    }
  }, [date]);

  const fetchDailyLtr = useCallback(async () => {
    if (!isAuth || !radioId || !date) return;

    setLoading(true);
    setInfo('');
    setBlocks([]);

    try {
      const API = (API_BASE_URL || '').replace(/\/+$/, '');
      const url = new URL(`${API}/radios/${encodeURIComponent(radioId)}/metrics/ltr/daily`);
      url.searchParams.set('date', date);

      const res = await authFetch(url.toString());
      const data = await res.json();

      if (!data || !Array.isArray(data.items) || !data.items.length) {
        setInfo(t('ltr.no_blocks'));
        setBlocks([]);
      } else {
        setBlocks(data.items);
      }
    } catch (e) {
      setInfo(t('network_error') || 'Помилка мережі');
      setBlocks([]);
    }

    setLoading(false);
  }, [isAuth, radioId, date, t]);

  const exportDisabled = !blocks.length || loading;
  const exportButtonStyle = buildPrimaryButtonStyle(exportDisabled);
  const filenameBase = buildFilenameBase('ltr', shortcode, date);

  if (!radioId) {
    return (
      <div style={widgetStyle}>
        <h2 style={{ marginBottom: 8 }}>{serviceName}</h2>
        <div style={{ color: '#e54848', fontWeight: 600, fontSize: 18 }}>
          {t('no_channel') || 'Оберіть радіостанцію у списку'}
        </div>
      </div>
    );
  }

  return (
    <div style={widgetStyle}>
      <div
        style={{
          width: '100%',
          padding: '8px 12px',
          borderRadius: 8,
          background: '#f8f8f8',
          color: '#1a1a1a',
          fontSize: 17,
          margin: '8px 0 18px 0',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{serviceName}</div>
        <div>{t('ltr.description')}</div>
      </div>

      <h2 style={{ marginBottom: 8 }}>{headingTitle}</h2>

      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          gap: ACTION_BUTTON_GAP,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontWeight: 500 }}>{t('ltr.date_label')}</span>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          style={inputStyle}
        />
        <button onClick={fetchDailyLtr} style={showBtn}>
          {t('show')}
        </button>
        <ExportButton
          data={blocks}
          buildDoc={(rows) =>
            buildLtrPdfDoc({
              blocks: rows,
              stationName,
              shortcode,
              date,
              strings: exportStrings,
              locale: exportLocale,
            })
          }
          buildWorkbook={(XLSX, rows) =>
            buildLtrWorkbook(XLSX, {
              blocks: rows,
              stationName,
              shortcode,
              date,
              strings: exportStrings,
              locale: exportLocale,
            })
          }
          filename={`${filenameBase}.pdf`}
          xlsxFilename={`${filenameBase}.xlsx`}
          label={t('export_pdf')}
          labelXlsx={t('export_excel')}
          style={exportButtonStyle}
          disabled={exportDisabled}
        />
      </div>

      {loading && (
        <div style={{ margin: '18px 0', fontWeight: 500 }}>
          {t('loading') || 'Завантаження...'}
        </div>
      )}
      {info && !loading && (
        <div style={{ margin: '18px 0', fontWeight: 500 }}>{info}</div>
      )}

      {!loading && blocks.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16 }}
          >
            <thead>
              <tr style={{ background: '#f0f0f0' }}>
                <th style={thTd}>{exportStrings.table.start}</th>
                <th style={thTd}>{exportStrings.table.end}</th>
                <th style={thTd}>{exportStrings.table.clips}</th>
                <th style={thTd}>{exportStrings.table.impressions}</th>
                <th style={thTd}>{exportStrings.table.completions}</th>
                <th style={thTd}>{exportStrings.table.ltr}</th>
              </tr>
            </thead>
            <tbody>
              {blocks.map((block, idx) => (
                <tr key={idx}>
                  <td style={thTd}>
                    {String(block.start || '').slice(0, 8)}
                  </td>
                  <td style={thTd}>
                    {String(block.end || '').slice(0, 8)}
                  </td>
                  <td style={thTd}>
                    {Array.isArray(block.clips) && block.clips.length > 0
                      ? block.clips.map((clip, j) => (
                          <span key={j}>
                            {clip}
                            <br />
                          </span>
                        ))
                      : '—'}
                  </td>
                  <td style={{ ...thTd, textAlign: 'center' }}>
                    {block.impressions ?? 0}
                  </td>
                  <td style={{ ...thTd, textAlign: 'center' }}>
                    {block.completions ?? 0}
                  </td>
                  <td style={{ ...thTd, textAlign: 'center' }}>
                    {block.ltr == null
                      ? '—'
                      : block.ltr.toFixed(1)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const widgetStyle = {
  maxWidth: 1100,
  margin: '40px auto',
  background: '#fff',
  borderRadius: 14,
  padding: 24,
  boxShadow: '0 2px 12px 0 #0001',
};

const inputStyle = {
  marginRight: 8,
  padding: 7,
  borderRadius: 4,
  border: '1px solid #ccc',
  fontSize: 16,
  minWidth: 130,
};

const ACTION_BUTTON_GAP = 12;

const primaryButtonBase = {
  border: 'none',
  padding: '8px 16px',
  borderRadius: 5,
  fontWeight: 600,
  minWidth: 150,
  textAlign: 'center',
};

function buildPrimaryButtonStyle(disabled = false) {
  return {
    ...primaryButtonBase,
    background: disabled ? '#9bbdfd' : '#3867fa',
    color: '#fff',
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}

const showBtn = buildPrimaryButtonStyle(false);

const thTd = {
  padding: '7px 12px',
  border: '1px solid #e0e0e0',
  textAlign: 'left',
  verticalAlign: 'top',
};

function buildLtrPdfDoc(params = {}) {
  const {
    blocks = [],
    stationName = '',
    shortcode = '',
    date = '',
    strings = {},
    locale = 'uk-UA',
  } = params;

  const rows = mapBlocksForExport(blocks, locale);
  const tableStrings = strings.table || {};

  const headerRow = [
    { text: tableStrings.start || 'Start', style: 'tableHeader' },
    { text: tableStrings.end || 'End', style: 'tableHeader' },
    { text: tableStrings.clips || 'Clips', style: 'tableHeader' },
    { text: tableStrings.impressions || 'Impressions', style: 'tableHeader' },
    { text: tableStrings.completions || 'Completions', style: 'tableHeader' },
    { text: tableStrings.ltr || 'LTR, %', style: 'tableHeader' },
  ];

  const bodyRows = rows.length
    ? rows.map((row) => [
        { text: row.start || '—', style: 'tableCell' },
        { text: row.end || '—', style: 'tableCell' },
        { text: row.clips || '—', style: 'tableCell' },
        { text: row.impressionsDisplay, style: 'tableCell', alignment: 'right' },
        { text: row.completionsDisplay, style: 'tableCell', alignment: 'right' },
        { text: row.ltrDisplay, style: 'tableCell', alignment: 'right' },
      ])
    : [[
        { text: strings.emptyExport || 'No data to export', colSpan: 6, style: 'emptyCell' },
        {},
        {},
        {},
        {},
        {},
      ]];

  const stationLine = stationName || shortcode ? `${strings.stationLabel || 'Station'}: ${stationName || shortcode}` : '';
  const dateLine = `${strings.dateLabel || 'Date:'} ${date || '—'}`;

  const content = [
    { text: strings.serviceName || 'LTR', style: 'title', margin: [0, 0, 0, 4] },
  ];

  if (stationLine) {
    content.push({ text: stationLine, style: 'subtitle' });
  }
  content.push({ text: dateLine, style: 'subtitle', margin: [0, 0, 0, 12] });
  content.push({
    table: {
      headerRows: 1,
      widths: [60, 60, '*', 'auto', 'auto', 'auto'],
      body: [headerRow, ...bodyRows],
    },
    layout: 'lightHorizontalLines',
  });

  return {
    pageMargins: [32, 40, 32, 40],
    content,
    defaultStyle: { fontSize: 10, color: '#1f1f1f' },
    styles: {
      title: { fontSize: 18, bold: true, color: '#111' },
      subtitle: { fontSize: 11, margin: [0, 0, 0, 6], color: '#333' },
      tableHeader: {
        fontSize: 10,
        bold: true,
        fillColor: '#f0f0f0',
        margin: [0, 4, 0, 4],
      },
      tableCell: { fontSize: 10 },
      emptyCell: { fontSize: 11, italics: true, color: '#777', alignment: 'center' },
    },
  };
}

function buildLtrWorkbook(XLSX, params = {}) {
  const {
    blocks = [],
    stationName = '',
    shortcode = '',
    date = '',
    strings = {},
    locale = 'uk-UA',
  } = params;

  const rows = mapBlocksForExport(blocks, locale);
  const tableStrings = strings.table || {};
  const header = [
    tableStrings.start || 'Start',
    tableStrings.end || 'End',
    tableStrings.clips || 'Clips',
    tableStrings.impressions || 'Impressions',
    tableStrings.completions || 'Completions',
    tableStrings.ltr || 'LTR, %',
  ];
  const tableRows = rows.map((row) => [
    row.start || '',
    row.end || '',
    row.clips || '',
    row.impressions,
    row.completions,
    row.ltrNumber ?? '',
  ]);

  const stationLine = stationName || shortcode ? `${strings.stationLabel || 'Station'}: ${stationName || shortcode}` : null;
  const prefixRows = [
    [strings.serviceName || 'LTR'],
    stationLine ? [stationLine] : null,
    [`${strings.dateLabel || 'Date:'} ${date || '—'}`],
    [''],
  ].filter(Boolean);

  const aoa = [...prefixRows, header, ...tableRows];

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [
    { wch: 12 },
    { wch: 12 },
    { wch: 40 },
    { wch: 15 },
    { wch: 15 },
    { wch: 10 },
  ];

  if (tableRows.length) {
    const headerRowIndex = prefixRows.length;
    const endRow = headerRowIndex + tableRows.length;
    ws['!autofilter'] = {
      ref: XLSX.utils.encode_range({
        s: { r: headerRowIndex, c: 0 },
        e: { r: endRow, c: header.length - 1 },
      }),
    };
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'LTR');
  return wb;
}

function mapBlocksForExport(blocks = [], locale = 'uk-UA') {
  const { numberFormatter, percentFormatter } = createFormatters(locale);
  return (blocks || []).map((block) => {
    const impressions = toNumber(block.impressions);
    const completions = toNumber(block.completions);
    const ltrNumberRaw = toFloatOrNull(block.ltr);
    const ltrNumber = ltrNumberRaw == null ? null : Number(ltrNumberRaw.toFixed(1));
    return {
      start: formatTime(block.start),
      end: formatTime(block.end),
      clips: joinClips(block.clips),
      impressions,
      completions,
      ltrNumber,
      impressionsDisplay: numberFormatter.format(impressions),
      completionsDisplay: numberFormatter.format(completions),
      ltrDisplay: ltrNumber == null ? '—' : percentFormatter.format(ltrNumber),
    };
  });
}

function formatTime(value) {
  return String(value || '').slice(0, 8);
}

function joinClips(clips) {
  if (!Array.isArray(clips) || !clips.length) return '—';
  return clips.join('\n');
}

function toNumber(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  return num;
}

function toFloatOrNull(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function resolveLocaleForFormatter(lang = 'uk') {
  const map = {
    uk: 'uk-UA',
    en: 'en-US',
    it: 'it-IT',
    fr: 'fr-FR',
  };
  const normalized = String(lang || '').toLowerCase();
  if (map[normalized]) return map[normalized];
  const prefix = normalized.split('-')[0];
  return map[prefix] || 'en-US';
}

function buildFormatter(locale, options) {
  const fallback = 'en-US';
  try {
    return new Intl.NumberFormat(locale || fallback, options);
  } catch {
    return new Intl.NumberFormat(fallback, options);
  }
}

function createFormatters(locale = 'uk-UA') {
  return {
    numberFormatter: buildFormatter(locale),
    percentFormatter: buildFormatter(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
  };
}

function buildFilenameBase(prefix, shortcode, date) {
  const clean = (val) =>
    String(val || '')
      .trim()
      .replace(/\s+/g, '_')
      .replace(/[^0-9A-Za-z_-]/g, '')
      .toLowerCase();
  const channel = clean(shortcode || 'channel');
  const safeDate = clean(date || '');
  return [prefix, channel, safeDate].filter(Boolean).join('_');
}
